import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listDownloads } from "@/lib/adminApi";
import { Download, Loader2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

export default function AdminDownloads() {
  const [data, setData] = useState(null);

  useEffect(() => {
    listDownloads().then(setData).catch(() => setData({ total: 0, stats: {}, requests: [] }));
  }, []);

  if (data === null) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="animate-spin text-blue" />
      </div>
    );
  }

  const statsEntries = Object.entries(data.stats || {}).sort((a, b) => b[1].count - a[1].count);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl">تحميلات الإصدارات</h1>
          <p className="text-blue/70 text-sm mt-1" data-testid="downloads-total">
            {data.total} طلب تحميل إجمالًا — تُسجَّل منذ تفعيل ميزة إرسال الرابط بالبريد
          </p>
        </div>
        <span className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2.5 text-sm font-semibold">
          <Download size={16} /> {data.total}
        </span>
      </div>

      {statsEntries.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {statsEntries.map(([slug, s]) => (
            <div key={slug} className="bg-white border border-navy/10 p-5" data-testid={`dl-stat-${slug}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-display text-navy">{s.count}</span>
                <span className="text-xs text-blue/70">تحميل</span>
              </div>
              <div className="mt-2 text-sm text-navy font-medium leading-snug">{s.title || slug}</div>
              <Link to={`/knowledge/${slug}`} target="_blank" className="mt-1 inline-block text-xs text-blue/60 hover:text-coral" dir="ltr">
                /{slug}
              </Link>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display text-lg mb-3">سجل الطلبات الأخيرة</h2>
      {data.requests.length === 0 ? (
        <div className="bg-white border border-navy/10 p-8 text-center text-blue/70" data-testid="downloads-empty">
          لا توجد طلبات تحميل بعد. تظهر هنا البريدات التي تطلب روابط الإصدارات.
        </div>
      ) : (
        <div className="bg-white border border-navy/10 overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-paper/50 text-blue/70">
              <tr>
                <th className="p-3 font-medium">البريد الإلكتروني</th>
                <th className="p-3 font-medium">الإصدار</th>
                <th className="p-3 font-medium w-28">حالة البريد</th>
                <th className="p-3 font-medium w-44">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {data.requests.map((r) => (
                <tr key={r.id} className="border-t border-navy/5 hover:bg-paper/30" data-testid={`dl-row-${r.id}`}>
                  <td className="p-3 font-medium text-navy" dir="ltr">{r.email}</td>
                  <td className="p-3 text-blue">{r.publication_title || r.slug}</td>
                  <td className="p-3 text-xs">
                    {r.email_status === "sent" ? (
                      <span className="text-emerald-700">أُرسل</span>
                    ) : r.email_status === "error" ? (
                      <span className="text-coral">تعذّر</span>
                    ) : (
                      <span className="text-blue/60">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-blue/70">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
